import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { ONE_HOUR_IN_MS } from '@/src/general/constants/time'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'

import { communityApi } from '../../../../api/communityApi'

type TCommunityViewsStore = {
  communityViews: Record<number, number>
  updateCommunityViews: (id: number, mutate?: VoidFunction) => void
}

export const useCommunityViewsStore = create(
  persist<TCommunityViewsStore>(
    (set) => ({
      communityViews: {},
      updateCommunityViews: (id, mutate) =>
        set(({ communityViews, ...state }) => {
          /** Community views within the last hour only */
          const updatedCommunityViews = Object.entries(communityViews).reduce<Record<number, number>>(
            (acc, [communityId, timestamp]) => {
              if (timestamp >= Date.now() - ONE_HOUR_IN_MS) {
                acc[Number(communityId)] = timestamp
              }
              return acc
            },
            {},
          )

          const lastView = updatedCommunityViews[id]

          if (!lastView) {
            mutate?.()
            updatedCommunityViews[id] = Date.now()
          }

          return { ...state, communityViews: updatedCommunityViews }
        }),
    }),
    { name: STORAGE_KEYS.COMMUNITY_VIEWS, storage: createJSONStorage(() => storage) },
  ),
)

export const useIncrementCommunityViews = (id: number) => {
  const { mutate } = useMutation({
    mutationFn: () => communityApi.incrementCommunityViews(id),
  })

  const { updateCommunityViews } = useCommunityViewsStore()

  useEffect(() => {
    updateCommunityViews(id, mutate)
  }, [id, mutate, updateCommunityViews])
}
